export const EXECUTION_COST_CONFIRMATION_MESSAGE =
  'Review the execution assumptions and tick the confirmation before running this backtest.';

function executionCostConfig(payload) {
  if (Array.isArray(payload?.strategies)) return payload.strategies.map((strategy) => strategy?.config);
  if (payload?.base) return [payload.base.config];
  return [payload?.config];
}

/**
 * Frontend preflight shared by every trading-simulation mode. The backend is
 * still authoritative; this prevents a known-invalid request from reaching it
 * and gives the user an actionable explanation instead of a silent click or a
 * generic HTTP 400.
 */
export function assertBacktestExecutionCosts(payload) {
  const configs = executionCostConfig(payload);
  if (!configs.length || configs.some((config) => !config || config.accounting_policy !== 'cash_long_v1')) {
    throw new Error(EXECUTION_COST_CONFIRMATION_MESSAGE);
  }
  for (const config of configs) {
    const costs = config.execution_costs;
    if (!costs || typeof costs.source !== 'string' || !costs.source.trim()) {
      throw new Error(EXECUTION_COST_CONFIRMATION_MESSAGE);
    }
    for (const key of ['commission_bps', 'slippage_bps', 'fixed_per_order']) {
      if (!Number.isFinite(costs[key]) || costs[key] < 0) {
        throw new Error(`Enter a valid non-negative execution assumption for ${key.replaceAll('_', ' ')}.`);
      }
    }
    if (costs.commission_bps + costs.slippage_bps >= 10000) {
      throw new Error('Combined commission and slippage assumptions must be below 10,000 basis points.');
    }
  }
}

/** One compiler for every studio mode. Fees are explicit research assumptions. */
export function engineConfig(config) {
  return { capital: Number(config.capital), max_positions: Number(config.maxPositions),
    sizing: config.sizing, accounting_policy: 'cash_long_v1', entry_fill: config.entryFill || 'next_open',
    default_sl_pct: Number(config.slPct) > 0 ? Number(config.slPct) / 100 : null,
    default_pt_pct: Number(config.ptPct) > 0 ? Number(config.ptPct) / 100 : null,
    default_max_hold_bars: Number(config.maxHoldBars) || null,
    execution_costs: config.costsAccepted ? { commission_bps: Number(config.commissionBps ?? 10),
      slippage_bps: Number(config.slippageBps ?? 5), fixed_per_order: Number(config.fixedFee ?? 0),
      source: 'User-confirmed research assumption; not a verified exchange/broker tariff' } : null };
}
